import { useRef, useState, type DragEvent, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  EventCard,
  DROP_RING,
  EVENT_CARD_MARKER,
  isCalendarDragType,
} from "./event-card";
import { minuteFromOffset } from "@/pages/brand/calendar-past";
import type { CalendarEvent } from "@content-os/types";

/** True when a click/drag landed on an event card rather than empty cell. */
function isOnEvent(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(`[${EVENT_CARD_MARKER}]`) !== null;
}

/**
 * One hour cell in the week/day time grid. Handles rescheduling drops and
 * click-to-create at 15-minute precision (snapped by cursor position), with a
 * thin guide line showing where the drop/click will land. Past cells are
 * greyed and inert; events inside any cell stay clickable.
 */
export function TimeSlotCell({
  dateKey,
  hour,
  past,
  tz,
  compact,
  frameClass,
  events,
  onEventClick,
  onSlotDrop,
  onSlotCreate,
}: {
  dateKey: string;
  hour: number;
  past: boolean;
  tz: string;
  compact?: boolean;
  /** Border/height/padding classes for this view (week vs day differ). */
  frameClass: string;
  events: CalendarEvent[];
  onEventClick: (e: CalendarEvent) => void;
  onSlotDrop: (dateKey: string, hour: number | null, minute: number, e: DragEvent) => void;
  onSlotCreate?: (dateKey: string, hour: number | null, minute: number) => void;
}) {
  const { t } = useTranslation("calendar");
  // Top offset (%) of the snap guide line while hovering/dragging; null hides it.
  const [guide, setGuide] = useState<number | null>(null);
  // Cell rect cached for the duration of a hover/drag so pointer-move handlers
  // don't force a layout read on every event; cleared when the cursor leaves.
  const rectRef = useRef<DOMRect | null>(null);
  // A pending document `dragend` cleanup for aborted drags (see onDragOver).
  const dragEndRef = useRef<(() => void) | null>(null);
  const creatable = !past && !!onSlotCreate;

  const quarterAt = (clientY: number, el: HTMLElement): number => {
    const rect = (rectRef.current ??= el.getBoundingClientRect());
    return minuteFromOffset(clientY - rect.top, rect.height);
  };
  const showGuide = (minute: number) => setGuide((minute / 60) * 100);
  const clearGuide = () => {
    rectRef.current = null;
    setGuide(null);
    if (dragEndRef.current) {
      document.removeEventListener("dragend", dragEndRef.current);
      dragEndRef.current = null;
    }
  };

  const dropProps = past
    ? {}
    : {
        onDragOver: (e: DragEvent<HTMLDivElement>) => {
          if (!isCalendarDragType(e.dataTransfer.types)) return;
          e.preventDefault();
          e.currentTarget.classList.add(...DROP_RING);
          showGuide(quarterAt(e.clientY, e.currentTarget));
          // An aborted drag (Esc / drop outside a target) fires `dragend` but
          // not this cell's dragleave/drop, so register a one-shot dragend to
          // clear the ring + guide left behind.
          if (!dragEndRef.current) {
            const el = e.currentTarget;
            const onEnd = () => {
              el.classList.remove(...DROP_RING);
              clearGuide();
            };
            dragEndRef.current = onEnd;
            document.addEventListener("dragend", onEnd);
          }
        },
        onDragLeave: (e: DragEvent<HTMLDivElement>) => {
          // Ignore bubbled dragleave when moving onto a child (event chip) —
          // otherwise the ring/guide flicker as the pointer crosses events.
          if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
          e.currentTarget.classList.remove(...DROP_RING);
          clearGuide();
        },
        onDrop: (e: DragEvent<HTMLDivElement>) => {
          const minute = quarterAt(e.clientY, e.currentTarget);
          e.currentTarget.classList.remove(...DROP_RING);
          clearGuide();
          e.preventDefault();
          onSlotDrop(dateKey, hour, minute, e);
        },
      };

  const clickProps = creatable
    ? {
        onClick: (e: MouseEvent<HTMLDivElement>) => {
          // Clicks on an event open the event; only empty cell space creates.
          if (isOnEvent(e.target)) return;
          onSlotCreate!(dateKey, hour, quarterAt(e.clientY, e.currentTarget));
        },
        onMouseMove: (e: MouseEvent<HTMLDivElement>) => {
          if (isOnEvent(e.target)) return setGuide(null);
          showGuide(quarterAt(e.clientY, e.currentTarget));
        },
        onMouseLeave: clearGuide,
      }
    : {};

  return (
    <div
      {...dropProps}
      {...clickProps}
      title={creatable ? t("page.createHint") : undefined}
      className={`relative overflow-hidden ${frameClass}${past ? " bg-muted/40" : creatable ? " cursor-pointer hover:bg-primary/5" : ""}`}
    >
      {guide !== null && (
        <div
          className="pointer-events-none absolute inset-x-0 h-px bg-primary/60"
          style={{ top: `${guide}%` }}
        />
      )}
      {events.map((ev) => (
        <EventCard key={ev.id} event={ev} onClick={onEventClick} compact={compact} tz={tz} />
      ))}
    </div>
  );
}
